<template>
  <div class="queues-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="Call Queues" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-left">
            <h1>Call Queues</h1>
            <p class="subtitle">Route inbound calls to agents with smart ring strategies.</p>
          </div>
          <div class="header-right">
            <button class="btn-refresh" @click="load" :disabled="loading">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
            </button>
            <button class="btn-new" @click="openCreate">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
              New Queue
            </button>
          </div>
        </div>

        <!-- Summary strip -->
        <div v-if="!loading && !error && queues.length" class="summary-strip">
          <div class="summary-pill">
            <span class="summary-num">{{ queues.length }}</span>
            <span class="summary-label">{{ queues.length === 1 ? 'Queue' : 'Queues' }}</span>
          </div>
          <div class="summary-sep"></div>
          <div class="summary-pill">
            <span class="summary-num">{{ totalMembers }}</span>
            <span class="summary-label">{{ totalMembers === 1 ? 'Agent Slot' : 'Agent Slots' }}</span>
          </div>
          <div class="summary-sep"></div>
          <div v-for="s in usedStrategies" :key="s.value" class="summary-strategy-tag" :style="{ background: s.color + '12', color: s.color, borderColor: s.color + '30' }">
            {{ s.emoji }} {{ s.label }}
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          <div>{{ error }}<br /><button class="retry-btn" @click="load">Retry</button></div>
        </div>

        <!-- Queue Grid -->
        <div v-else-if="queues.length" class="queue-grid">
          <div
            v-for="queue in queues"
            :key="queue.id"
            class="queue-card"
            :style="{ '--accent': strategyMap[queue.strategy] ? strategyMap[queue.strategy].color : '#6B7280' }"
          >
            <!-- Card top bar -->
            <div class="card-top">
              <div class="queue-identity">
                <div class="strategy-emoji-badge">{{ strategyMap[queue.strategy] ? strategyMap[queue.strategy].emoji : '📞' }}</div>
                <div class="queue-names">
                  <div class="queue-display-name">{{ queue.display_name }}</div>
                  <div class="queue-tech-name"><code>{{ queue.name }}</code></div>
                </div>
              </div>
              <div class="card-actions">
                <button class="card-btn" @click="openEdit(queue)" title="Edit queue">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                </button>
                <button class="card-btn card-btn-danger" @click="confirmDelete(queue)" title="Delete queue">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                </button>
              </div>
            </div>

            <!-- Chips row -->
            <div class="card-chips">
              <span
                class="chip chip-strategy"
                :style="strategyMap[queue.strategy] ? { background: strategyMap[queue.strategy].color + '15', color: strategyMap[queue.strategy].color, borderColor: strategyMap[queue.strategy].color + '35' } : {}"
              >{{ strategyMap[queue.strategy] ? strategyMap[queue.strategy].label : queue.strategy }}</span>
              <span class="chip">⏱ {{ queue.timeout }}s ring</span>
              <span class="chip">{{ queue.maxlen === 0 ? '∞ unlimited' : queue.maxlen + ' max' }}</span>
              <span v-if="queue.wrapuptime > 0" class="chip">↩ {{ queue.wrapuptime }}s wrap</span>
              <span v-if="queue.announce_position" class="chip chip-teal">📍 position</span>
              <span v-if="queue.announce_holdtime" class="chip chip-teal">⏰ hold time</span>
            </div>

            <!-- Members section -->
            <div class="members-section">
              <div class="members-header-row">
                <span class="members-label">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="opacity:0.6"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
                  {{ queue.members.length }} {{ queue.members.length === 1 ? 'agent' : 'agents' }}
                </span>
              </div>

              <div class="members-tags">
                <!-- Member chips -->
                <div
                  v-for="member in queue.members"
                  :key="member.extension"
                  class="member-tag"
                  :class="{ 'member-removing': removingKey === queue.id + ':' + member.extension }"
                >
                  <span class="member-tag-ext">{{ member.extension }}</span>
                  <span v-if="member.agent_name" class="member-tag-name">{{ member.agent_name }}</span>
                  <button
                    class="member-tag-remove"
                    @click.stop="removeMember(queue, member.extension)"
                    :disabled="removingKey === queue.id + ':' + member.extension"
                    title="Remove from queue"
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  </button>
                </div>

                <!-- Add agent button + picker -->
                <div class="picker-anchor" @click.stop>
                  <button class="btn-add-agent" @click.stop="togglePicker(queue.id)">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                    Add Agent
                  </button>

                  <Transition name="picker-pop">
                    <div v-if="pickerFor === queue.id" class="agent-picker">
                      <div class="picker-search-row">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        <input
                          v-model="pickerSearch"
                          class="picker-search"
                          placeholder="Search agents..."
                          autofocus
                          @click.stop
                        />
                      </div>
                      <div class="picker-list">
                        <div v-if="availableAgents(queue).length === 0" class="picker-empty">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          {{ connectedAgents.length === 0 ? 'No connected agents yet' : 'All connected agents already in this queue' }}
                        </div>
                        <button
                          v-for="agent in availableAgents(queue)"
                          :key="agent.agent_id"
                          class="picker-item"
                          :disabled="addingMember === queue.id + ':' + agent.extension"
                          @click.stop="addMember(queue, agent)"
                        >
                          <div class="picker-item-avatar">{{ agent.name.charAt(0).toUpperCase() }}</div>
                          <div class="picker-item-info">
                            <span class="picker-item-name">{{ agent.name }}</span>
                            <span class="picker-item-ext">ext {{ agent.extension }}</span>
                          </div>
                          <span v-if="addingMember === queue.id + ':' + agent.extension" class="picker-item-spinner"></span>
                        </button>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty -->
        <div v-else class="empty-state">
          <div class="empty-graphic">
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h3>No queues yet</h3>
          <p>Create a queue to start routing inbound calls to the right agents.</p>
          <button class="btn-new" @click="openCreate">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Create First Queue
          </button>
        </div>

      </main>
    </div>

    <!-- Create / Edit Modal -->
    <Transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <div class="modal-header">
            <div class="modal-title-group">
              <h2>{{ isEdit ? 'Edit Queue' : 'New Queue' }}</h2>
              <p class="modal-subtitle">{{ isEdit ? 'Update queue settings — members are managed from the card.' : 'Configure a new call queue for your agents.' }}</p>
            </div>
            <button class="modal-close" @click="closeModal">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>

          <div class="modal-body">

            <!-- Basic Info -->
            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">
                  Queue ID
                  <span class="label-hint">numbers only</span>
                </label>
                <input
                  v-model="form.name"
                  class="form-input"
                  :class="{ 'input-error': nameError }"
                  placeholder="e.g. 6001"
                  inputmode="numeric"
                  @input="sanitizeName"
                  :readonly="isEdit"
                  :title="isEdit ? 'Queue ID cannot be changed after creation' : ''"
                />
                <div v-if="nameError" class="field-error">{{ nameError }}</div>
                <div v-if="isEdit" class="field-hint">Queue ID is fixed after creation</div>
              </div>
              <div class="form-group">
                <label class="form-label">Display Name</label>
                <input v-model="form.display_name" class="form-input" placeholder="e.g. Sales Team" />
              </div>
            </div>

            <!-- Strategy picker -->
            <div class="form-group">
              <label class="form-label">Ring Strategy</label>
              <div class="strategy-grid">
                <button
                  v-for="s in strategies"
                  :key="s.value"
                  type="button"
                  class="strategy-card"
                  :class="{ 'strategy-selected': form.strategy === s.value }"
                  :style="form.strategy === s.value ? { borderColor: s.color, background: s.color + '0e' } : {}"
                  @click="form.strategy = s.value"
                >
                  <div class="sc-emoji">{{ s.emoji }}</div>
                  <div class="sc-label" :style="form.strategy === s.value ? { color: s.color } : {}">{{ s.label }}</div>
                  <div class="sc-desc">{{ s.desc }}</div>
                  <span v-if="s.recommended" class="sc-rec">★ recommended</span>
                </button>
              </div>
            </div>

            <!-- Timing -->
            <div class="section-divider">
              <span>Timing</span>
            </div>
            <div class="form-row-3">
              <div class="form-group">
                <label class="form-label">Ring Timeout</label>
                <div class="suffix-input">
                  <input v-model.number="form.timeout" type="number" min="5" max="300" class="form-input" />
                  <span class="suffix">sec</span>
                </div>
                <div class="field-hint">Seconds per agent before trying the next</div>
              </div>
              <div class="form-group">
                <label class="form-label">Wrapup Time</label>
                <div class="suffix-input">
                  <input v-model.number="form.wrapuptime" type="number" min="0" max="300" class="form-input" />
                  <span class="suffix">sec</span>
                </div>
                <div class="field-hint">Cooldown after a call ends</div>
              </div>
              <div class="form-group">
                <label class="form-label">Max Callers Waiting</label>
                <input v-model.number="form.maxlen" type="number" min="0" class="form-input" placeholder="0" />
                <div class="field-hint">0 = unlimited</div>
              </div>
            </div>

            <!-- Announcements -->
            <div class="section-divider">
              <span>Caller Announcements</span>
            </div>
            <div class="toggles-group">
              <div class="toggle-row">
                <div class="toggle-info">
                  <span class="toggle-title">Announce hold time</span>
                  <span class="toggle-desc">Tell callers their estimated wait time</span>
                </div>
                <div class="toggle-switch" :class="{ on: form.announce_holdtime }" @click="form.announce_holdtime = !form.announce_holdtime">
                  <div class="toggle-knob"></div>
                </div>
              </div>
              <div class="toggle-row">
                <div class="toggle-info">
                  <span class="toggle-title">Announce position</span>
                  <span class="toggle-desc">Tell callers their position in the queue</span>
                </div>
                <div class="toggle-switch" :class="{ on: form.announce_position }" @click="form.announce_position = !form.announce_position">
                  <div class="toggle-knob"></div>
                </div>
              </div>
            </div>

            <!-- Error -->
            <div v-if="modalError" class="save-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              {{ modalError }}
            </div>

          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal" :disabled="saving">Cancel</button>
            <button class="btn-save" @click="saveQueue" :disabled="saving || !form.name.trim() || !!nameError">
              <span v-if="saving" class="btn-spinner"></span>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              {{ saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Queue') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete Confirm Modal -->
    <Transition name="modal-fade">
      <div v-if="deletingQueue" class="modal-overlay" @click.self="deletingQueue = null">
        <div class="modal modal-sm">
          <div class="modal-header">
            <h2>Delete Queue?</h2>
            <button class="modal-close" @click="deletingQueue = null">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>
          <div class="modal-body">
            <div class="delete-warning">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#EF4444"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            </div>
            <p>Are you sure you want to delete <strong>{{ deletingQueue.display_name }}</strong>? This will also remove all its agent members.</p>
            <div v-if="deleteError" class="save-error">{{ deleteError }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="deletingQueue = null" :disabled="deleteLoading">Cancel</button>
            <button class="btn-delete" @click="doDelete" :disabled="deleteLoading">
              <span v-if="deleteLoading" class="btn-spinner"></span>
              {{ deleteLoading ? 'Deleting...' : 'Yes, Delete' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

const STRATEGIES = [
  { value: 'rrmemory', label: 'Round Robin', emoji: '🔄', color: '#10B981', desc: 'Remembers position, distributes evenly', recommended: true },
  { value: 'ringall', label: 'Ring All', emoji: '🔔', color: '#F59E0B', desc: 'All agents ring at the same time' },
  { value: 'leastrecent', label: 'Least Recent', emoji: '🎯', color: '#8B5CF6', desc: 'Agent who least recently had a call' },
  { value: 'fewestcalls', label: 'Fewest Calls', emoji: '📊', color: '#3B82F6', desc: 'Agent with the fewest completed calls' },
  { value: 'linear', label: 'Linear', emoji: '📋', color: '#6B7280', desc: 'Always rings agents in defined order' },
  { value: 'random', label: 'Weighted Random', emoji: '🎲', color: '#EC4899', desc: 'Random, weighted by penalty value' }
]

function blankForm () {
  return {
    name: '',
    display_name: '',
    strategy: 'rrmemory',
    timeout: 30,
    maxlen: 0,
    wrapuptime: 0,
    music_on_hold: 'default',
    announce_holdtime: false,
    announce_position: false
  }
}

export default {
  name: 'IVRQueuesView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      queues: [],
      agents: [],
      loading: true,
      error: null,
      strategies: STRATEGIES,
      // Queue modal
      showModal: false,
      isEdit: false,
      editingId: null,
      form: blankForm(),
      saving: false,
      modalError: null,
      // Delete
      deletingQueue: null,
      deleteLoading: false,
      deleteError: null,
      // Agent picker (inline per card)
      pickerFor: null,
      pickerSearch: '',
      addingMember: null,
      removingKey: null
    }
  },

  computed: {
    strategyMap () {
      const map = {}
      for (const s of STRATEGIES) {
        map[s.value] = s
      }
      return map
    },
    totalMembers () {
      return this.queues.reduce((sum, q) => sum + q.members.length, 0)
    },
    usedStrategies () {
      const seen = new Set()
      const result = []
      for (const q of this.queues) {
        if (!seen.has(q.strategy)) {
          seen.add(q.strategy)
          const s = this.strategyMap[q.strategy]
          if (s) result.push(s)
        }
      }
      return result
    },
    connectedAgents () {
      return this.agents.filter(a => a.connected && a.extension)
    },
    nameError () {
      if (!this.form.name) return null
      if (!/^[0-9]+$/.test(this.form.name)) return 'Numbers only'
      return null
    }
  },

  mounted () {
    this._clickAway = () => { this.pickerFor = null }
    window.addEventListener('click', this._clickAway)
    this.load()
  },

  beforeUnmount () {
    window.removeEventListener('click', this._clickAway)
  },

  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const [queuesRes, agentsRes] = await Promise.all([
          IVRService.getQueues(),
          IVRService.getAgentsExtensions()
        ])
        this.queues = queuesRes.queues || []
        this.agents = agentsRes.agents || []
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    availableAgents (queue) {
      const inQueue = new Set(queue.members.map(m => m.extension))
      const q = this.pickerSearch.toLowerCase().trim()
      return this.connectedAgents
        .filter(a => !inQueue.has(a.extension))
        .filter(a => !q || a.name.toLowerCase().includes(q) || a.extension.includes(q) || (a.agent_code && a.agent_code.toLowerCase().includes(q)))
    },

    togglePicker (queueId) {
      if (this.pickerFor === queueId) {
        this.pickerFor = null
      } else {
        this.pickerFor = queueId
        this.pickerSearch = ''
      }
    },

    async addMember (queue, agent) {
      const key = queue.id + ':' + agent.extension
      this.addingMember = key
      try {
        await IVRService.addQueueMember(queue.id, {
          extension: agent.extension,
          agent_name: agent.name,
          penalty: 0
        })
        const q = this.queues.find(x => x.id === queue.id)
        if (q) {
          const existing = q.members.findIndex(m => m.extension === agent.extension)
          if (existing === -1) {
            q.members.push({ extension: agent.extension, agent_name: agent.name, penalty: 0 })
          }
        }
        this.pickerFor = null
      } catch (e) {
        console.error('Failed to add member:', e.message)
      } finally {
        this.addingMember = null
      }
    },

    async removeMember (queue, extension) {
      const key = queue.id + ':' + extension
      this.removingKey = key
      try {
        await IVRService.removeQueueMember(queue.id, extension)
        const q = this.queues.find(x => x.id === queue.id)
        if (q) {
          q.members = q.members.filter(m => m.extension !== extension)
        }
      } catch (e) {
        console.error('Failed to remove member:', e.message)
      } finally {
        this.removingKey = null
      }
    },

    openCreate () {
      this.isEdit = false
      this.editingId = null
      this.form = blankForm()
      this.modalError = null
      this.showModal = true
    },

    openEdit (queue) {
      this.isEdit = true
      this.editingId = queue.id
      this.form = {
        name: queue.name,
        display_name: queue.display_name,
        strategy: queue.strategy,
        timeout: queue.timeout,
        maxlen: queue.maxlen,
        wrapuptime: queue.wrapuptime,
        music_on_hold: queue.music_on_hold || 'default',
        announce_holdtime: !!queue.announce_holdtime,
        announce_position: !!queue.announce_position
      }
      this.modalError = null
      this.showModal = true
    },

    closeModal () {
      if (this.saving) return
      this.showModal = false
    },

    sanitizeName () {
      this.form.name = this.form.name.replace(/[^0-9]/g, '')
    },

    async saveQueue () {
      if (this.nameError) return
      this.saving = true
      this.modalError = null
      try {
        const payload = { ...this.form }
        if (this.isEdit) {
          delete payload.name
          await IVRService.updateQueue(this.editingId, payload)
          const idx = this.queues.findIndex(q => q.id === this.editingId)
          if (idx !== -1) {
            this.queues[idx] = { ...this.queues[idx], ...payload }
          }
        } else {
          const res = await IVRService.createQueue(payload)
          this.queues.push({
            id: res.id,
            ...payload,
            members: []
          })
        }
        this.showModal = false
      } catch (e) {
        this.modalError = e.message
      } finally {
        this.saving = false
      }
    },

    confirmDelete (queue) {
      this.deletingQueue = queue
      this.deleteError = null
    },

    async doDelete () {
      this.deleteLoading = true
      this.deleteError = null
      try {
        await IVRService.deleteQueue(this.deletingQueue.id)
        this.queues = this.queues.filter(q => q.id !== this.deletingQueue.id)
        this.deletingQueue = null
      } catch (e) {
        this.deleteError = e.message
      } finally {
        this.deleteLoading = false
      }
    }
  }
}
</script>

<style scoped>
.queues-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
}
.header-left h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.btn-refresh {
  width: 36px;
  height: 36px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.btn-refresh:hover:not(:disabled) { background: var(--bg-tertiary); color: var(--text-primary); }
.btn-refresh:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-new {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #3B82F6 0%, #6366F1 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
}
.btn-new:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4); }

/* Summary strip */
.summary-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
}
.summary-pill {
  display: flex;
  align-items: baseline;
  gap: 5px;
}
.summary-num {
  font-size: 22px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
}
.summary-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}
.summary-sep {
  width: 1px;
  height: 20px;
  background: var(--border-primary);
  margin: 0 4px;
}
.summary-strategy-tag {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid transparent;
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 44px;
  height: 44px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.error-state {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 18px 20px;
  color: #DC2626;
  font-size: 14px;
}
.retry-btn {
  margin-top: 8px;
  padding: 5px 14px;
  border: 1px solid #FECACA;
  border-radius: 6px;
  background: transparent;
  color: #DC2626;
  cursor: pointer;
  font-size: 13px;
}
.retry-btn:hover { background: #FEE2E2; }
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 80px 20px;
  text-align: center;
}
.empty-graphic {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  opacity: 0.5;
}
.empty-state h3 { font-size: 20px; font-weight: 700; color: var(--text-primary); margin: 0; }
.empty-state p { font-size: 14px; color: var(--text-secondary); margin: 0; max-width: 360px; }

/* Queue grid */
.queue-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
  gap: 20px;
}

/* Queue card */
.queue-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, transform 0.2s;
  border-left: 4px solid var(--accent, #6B7280);
  position: relative;
}
.queue-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Card top */
.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 18px 12px;
  gap: 10px;
}
.queue-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.strategy-emoji-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent, #6B7280) 12%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--accent, #6B7280) 20%, transparent);
}
.queue-names { min-width: 0; }
.queue-display-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.queue-tech-name {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}
.queue-tech-name code {
  font-family: monospace;
  font-size: 12px;
  background: var(--bg-secondary);
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
}
.card-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.card-btn {
  width: 30px;
  height: 30px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.15s;
}
.card-btn:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.card-btn-danger:hover { background: #FEF2F2; color: #DC2626; border-color: #FECACA; }

/* Chips */
.card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 18px 14px;
}
.chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
  white-space: nowrap;
}
.chip-strategy {
  border: 1px solid transparent;
}
.chip-teal {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.2);
  color: #059669;
}

/* Members */
.members-section {
  flex: 1;
  border-top: 1px solid var(--border-primary);
  padding: 12px 18px 16px;
  background: var(--bg-secondary);
}
.members-header-row {
  margin-bottom: 10px;
}
.members-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.members-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
}
.member-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 5px 8px 5px 10px;
  font-size: 13px;
  transition: all 0.15s;
}
.member-tag:hover { border-color: var(--border-secondary, var(--border-primary)); }
.member-removing { opacity: 0.4; }
.member-tag-ext {
  font-family: monospace;
  font-weight: 700;
  font-size: 12px;
  color: #3B82F6;
  background: rgba(59, 130, 246, 0.08);
  padding: 1px 6px;
  border-radius: 4px;
}
.member-tag-name {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}
.member-tag-remove {
  width: 18px;
  height: 18px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  border-radius: 4px;
  padding: 0;
  transition: all 0.15s;
}
.member-tag-remove:hover { background: #FEF2F2; color: #EF4444; }
.member-tag-remove:disabled { opacity: 0.3; cursor: not-allowed; }

/* Add agent picker */
.picker-anchor {
  position: relative;
}
.btn-add-agent {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border: 1px dashed var(--border-primary);
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  transition: all 0.15s;
}
.btn-add-agent:hover {
  border-color: #3B82F6;
  color: #3B82F6;
  background: rgba(59, 130, 246, 0.05);
}
.agent-picker {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  width: 260px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  z-index: 200;
  overflow: hidden;
}
.picker-search-row {
  position: relative;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
  background: var(--bg-secondary);
}
.picker-search-row svg { color: var(--text-secondary); flex-shrink: 0; }
.picker-search {
  flex: 1;
  padding: 10px 0;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--text-primary);
  outline: none;
}
.picker-list {
  max-height: 200px;
  overflow-y: auto;
}
.picker-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  text-align: center;
  font-size: 12px;
  color: var(--text-secondary);
}
.picker-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border: none;
  border-bottom: 1px solid var(--border-primary);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}
.picker-item:last-child { border-bottom: none; }
.picker-item:hover:not(:disabled) { background: var(--bg-secondary); }
.picker-item:disabled { opacity: 0.5; cursor: not-allowed; }
.picker-item-avatar {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #3B82F6;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.picker-item-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}
.picker-item-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.picker-item-ext {
  font-size: 11px;
  font-family: monospace;
  color: var(--text-secondary);
}
.picker-item-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

/* Picker pop transition */
.picker-pop-enter-active, .picker-pop-leave-active { transition: all 0.18s ease; }
.picker-pop-enter-from, .picker-pop-leave-to { opacity: 0; transform: translateY(6px) scale(0.97); }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.modal {
  background: var(--bg-primary);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 580px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-sm { max-width: 420px; }
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 26px 0;
  gap: 12px;
}
.modal-title-group h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 3px;
}
.modal-subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}
.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bg-secondary);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
  flex-shrink: 0;
}
.modal-close:hover { background: var(--border-primary); color: var(--text-primary); }
.modal-body {
  padding: 20px 26px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 26px 22px;
  border-top: 1px solid var(--border-primary);
}

/* Form */
.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.form-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
}
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
}
.label-hint {
  font-size: 11px;
  font-weight: 400;
  color: var(--text-secondary);
  opacity: 0.7;
}
.form-input {
  padding: 10px 14px;
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  width: 100%;
}
.form-input:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.form-input.input-error { border-color: #EF4444; }
.form-input[readonly] { opacity: 0.55; cursor: not-allowed; }
.suffix-input { position: relative; }
.suffix-input .form-input { padding-right: 44px; }
.suffix {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  pointer-events: none;
}
.field-error { font-size: 12px; color: #EF4444; }
.field-hint { font-size: 11px; color: var(--text-secondary); opacity: 0.7; }

/* Strategy grid */
.strategy-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.strategy-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px;
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  background: var(--bg-secondary);
  cursor: pointer;
  text-align: left;
  transition: all 0.18s;
}
.strategy-card:hover:not(.strategy-selected) { border-color: var(--border-secondary, var(--border-primary)); background: var(--bg-tertiary); }
.strategy-selected { box-shadow: 0 0 0 1px currentColor; }
.sc-emoji { font-size: 20px; line-height: 1; margin-bottom: 2px; }
.sc-label { font-size: 13px; font-weight: 700; color: var(--text-primary); }
.sc-desc { font-size: 11px; color: var(--text-secondary); line-height: 1.4; }
.sc-rec {
  font-size: 10px;
  font-weight: 700;
  color: #10B981;
  background: rgba(16, 185, 129, 0.1);
  padding: 2px 6px;
  border-radius: 20px;
  margin-top: 4px;
}

/* Section divider */
.section-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 2px 0 -4px;
}
.section-divider::before, .section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-primary);
}

/* Toggles */
.toggles-group { display: flex; flex-direction: column; gap: 12px; }
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
}
.toggle-info { display: flex; flex-direction: column; gap: 2px; }
.toggle-title { font-size: 14px; font-weight: 600; color: var(--text-primary); }
.toggle-desc { font-size: 12px; color: var(--text-secondary); }
.toggle-switch {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: var(--border-primary);
  position: relative;
  cursor: pointer;
  transition: background 0.25s;
  flex-shrink: 0;
}
.toggle-switch.on { background: #10B981; }
.toggle-knob {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  top: 3px;
  left: 3px;
  transition: transform 0.25s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
.toggle-switch.on .toggle-knob { transform: translateX(20px); }

/* Save/Cancel/Delete buttons */
.btn-cancel {
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.btn-cancel:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #3B82F6, #6366F1);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}
.btn-save:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(99, 102, 241, 0.4); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.btn-delete {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  background: #EF4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}
.btn-delete:hover:not(:disabled) { background: #DC2626; }
.btn-delete:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* Delete warning */
.delete-warning {
  display: flex;
  justify-content: center;
  margin-bottom: 6px;
}
.modal-body p {
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  margin: 0;
  line-height: 1.6;
}
.modal-body p strong { color: var(--text-primary); }
.save-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #DC2626;
}

/* Modal fade transition */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
</style>
